import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Mail, Phone, Calendar, Loader2 } from "lucide-react";
import { Logo } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

export const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem("hylasu-admin-token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState(null);

  const loadQuotes = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${t}` } });
      setQuotes(data);
      setError("");
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("hylasu-admin-token");
        setToken("");
        setQuotes(null);
      } else {
        setError("No se pudieron cargar las cotizaciones");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadQuotes(token);
  }, [token, loadQuotes]);

  const login = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("hylasu-admin-token", data.token);
      setToken(data.token);
      setPassword("");
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("hylasu-admin-token");
    setToken("");
    setQuotes(null);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7] px-6 dark:bg-[#0C0A09]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm rounded-3xl border border-stone-200 bg-white p-8 shadow-xl dark:border-stone-800 dark:bg-stone-900"
        >
          <div className="mb-6 flex justify-center"><Logo /></div>
          <h1 className="text-center font-serif text-2xl text-stone-900 dark:text-stone-50">Área privada</h1>
          <p className="mt-1 text-center text-sm text-stone-500 dark:text-stone-400">Solicitudes de cotización</p>
          <div className="mt-6 space-y-3">
            <input
              data-testid="admin-password-input"
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors focus:border-amber-500 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
            />
            {error && <p data-testid="admin-login-error" className="text-center text-xs text-red-500">{error}</p>}
            <button
              data-testid="admin-login-button"
              onClick={login}
              disabled={loading || !password}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-stone-900 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 enabled:hover:bg-amber-700 disabled:opacity-50 dark:bg-stone-50 dark:text-stone-900"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <><Lock size={14} /> Entrar</>}
            </button>
            <a href="/" className="block text-center text-xs text-stone-400 transition-colors hover:text-stone-600">← Volver a la tienda</a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 py-10 dark:bg-[#0C0A09]">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <Logo />
          <div className="flex gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={() => loadQuotes(token)}
              className="flex items-center gap-2 rounded-full border border-stone-200 px-4 py-2 text-xs font-medium text-stone-600 transition-colors hover:border-stone-400 dark:border-stone-700 dark:text-stone-300"
            >
              <RefreshCw size={13} /> Actualizar
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-stone-200 px-4 py-2 text-xs font-medium text-stone-600 transition-colors hover:border-stone-400 dark:border-stone-700 dark:text-stone-300"
            >
              <LogOut size={13} /> Salir
            </button>
          </div>
        </div>
        <h1 className="mt-10 font-serif text-3xl font-light text-stone-900 dark:text-stone-50 sm:text-4xl">
          Solicitudes de cotización
        </h1>
        <p className="mt-2 text-sm text-stone-500 dark:text-stone-400" data-testid="admin-quotes-count">
          {quotes ? `${quotes.length} solicitud${quotes.length === 1 ? "" : "es"} recibida${quotes.length === 1 ? "" : "s"}` : "Cargando..."}
        </p>

        {loading && !quotes && <p className="mt-10 text-sm text-stone-400">Cargando...</p>}
        {error && <p className="mt-10 text-sm text-red-500">{error}</p>}

        <div className="mt-8 space-y-4">
          {quotes?.length === 0 && (
            <p data-testid="admin-quotes-empty" className="rounded-2xl border border-dashed border-stone-300 p-10 text-center text-sm text-stone-400 dark:border-stone-700">
              Aún no hay solicitudes. Las que lleguen desde el formulario aparecerán aquí.
            </p>
          )}
          {quotes?.map((q) => (
            <motion.article
              key={q.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`admin-quote-${q.id}`}
              className="rounded-2xl border border-stone-200 bg-white p-6 dark:border-stone-800 dark:bg-stone-900"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-xl text-stone-900 dark:text-stone-50">{q.name}</h2>
                  <span className="mt-1 inline-block rounded-full bg-amber-100 px-3 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-800 dark:bg-amber-500/15 dark:text-amber-400">
                    {q.project_type}
                  </span>
                </div>
                <span className="flex items-center gap-1.5 text-xs text-stone-400">
                  <Calendar size={12} /> {formatDate(q.created_at)}
                </span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-stone-600 dark:text-stone-300">{q.message}</p>
              <div className="mt-4 flex flex-wrap gap-4 text-xs text-stone-500 dark:text-stone-400">
                {q.email && (
                  <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 transition-colors hover:text-amber-700 dark:hover:text-amber-400">
                    <Mail size={13} /> {q.email}
                  </a>
                )}
                {q.phone && (
                  <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 transition-colors hover:text-amber-700 dark:hover:text-amber-400">
                    <Phone size={13} /> {q.phone}
                  </a>
                )}
              </div>
            </motion.article>
          ))}
        </div>
        <a href="/" className="mt-10 inline-block text-xs text-stone-400 transition-colors hover:text-stone-600">← Volver a la tienda</a>
      </div>
    </div>
  );
};
